// @ts-nocheck
import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import type { CatalogDivision, Sport } from "@/types/events";

interface DivisionWithSports extends CatalogDivision {
  sport_ids: string[];
}

export default function DivisionsCatalogPage() {
  const { organization } = useOrganization();
  const orgId = organization.id;

  const [sports, setSports] = useState<Sport[]>([]);
  const [divisions, setDivisions] = useState<DivisionWithSports[]>([]);
  const [activeFilterSportId, setActiveFilterSportId] = useState<string | "all">("all");
  const [loading, setLoading] = useState(false);

  // Add form
  const [newLabel, setNewLabel] = useState("");
  const [newSportIds, setNewSportIds] = useState<string[]>([]);

  // Edit dialog
  const [editTarget, setEditTarget] = useState<DivisionWithSports | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editSportIds, setEditSportIds] = useState<string[]>([]);

  const [deleteTarget, setDeleteTarget] = useState<DivisionWithSports | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkSportIds, setBulkSportIds] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("sports").select("id, name, slug").order("name");
      setSports(data ?? []);
    })();
  }, []);

  const loadDivisions = async () => {
    if (!orgId) return;
    setLoading(true);
    const { data: divs, error } = await supabase
      .from("division_catalog")
      .select("*")
      .eq("organization_id", orgId)
      .eq("is_active", true)
      .order("display_order");
    if (error) {
      toast.error("Failed to load divisions");
      setLoading(false);
      return;
    }
    const ids = (divs ?? []).map((d: any) => d.id);
    let mapping: Record<string, string[]> = {};
    if (ids.length > 0) {
      const { data: links } = await supabase
        .from("division_catalog_sports")
        .select("division_id, sport_id")
        .in("division_id", ids);
      (links ?? []).forEach((l: any) => {
        mapping[l.division_id] = mapping[l.division_id] || [];
        mapping[l.division_id].push(l.sport_id);
      });
    }
    const merged: DivisionWithSports[] = (divs ?? []).map((d: any) => ({
      ...d,
      sport_ids: mapping[d.id] ?? (d.sport_id ? [d.sport_id] : []),
    }));
    setDivisions(merged);
    setLoading(false);
  };

  useEffect(() => {
    loadDivisions();
  }, [orgId]);

  // Sports that actually have divisions assigned
  const sportsWithDivisions = useMemo(() => {
    const usedIds = new Set<string>();
    divisions.forEach((d) => d.sport_ids.forEach((id) => usedIds.add(id)));
    return sports.filter((s) => usedIds.has(s.id));
  }, [sports, divisions]);

  const filteredDivisions = useMemo(() => {
    if (activeFilterSportId === "all") return divisions;
    return divisions.filter((d) => d.sport_ids.includes(activeFilterSportId));
  }, [divisions, activeFilterSportId]);

  const sportName = (id: string) => sports.find((s) => s.id === id)?.name ?? "";

  const syncSportLinks = async (divisionId: string, sportIds: string[]) => {
    await supabase.from("division_catalog_sports").delete().eq("division_id", divisionId);
    if (sportIds.length > 0) {
      await supabase
        .from("division_catalog_sports")
        .insert(sportIds.map((sport_id) => ({ division_id: divisionId, sport_id })));
    }
  };

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return toast.error("Enter a name");
    if (newSportIds.length === 0) return toast.error("Select at least one sport");
    const nextOrder = divisions.length > 0 ? Math.max(...divisions.map((d) => d.display_order)) + 1 : 0;
    const { data, error } = await supabase
      .from("division_catalog")
      .insert({
        organization_id: orgId,
        sport_id: newSportIds[0], // legacy required column
        label,
        display_order: nextOrder,
      })
      .select()
      .single();
    if (error || !data) {
      toast.error(error?.message ?? "Failed");
      return;
    }
    await syncSportLinks(data.id, newSportIds);
    toast.success(`Added "${label}"`);
    setNewLabel("");
    setNewSportIds([]);
    loadDivisions();
  };

  const handleBulkAdd = async () => {
    const labels = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (labels.length === 0) return;
    if (bulkSportIds.length === 0) return toast.error("Select at least one sport");
    const baseOrder = divisions.length > 0 ? Math.max(...divisions.map((d) => d.display_order)) + 1 : 0;
    const rows = labels.map((label, i) => ({
      organization_id: orgId,
      sport_id: bulkSportIds[0],
      label,
      display_order: baseOrder + i,
    }));
    const { data, error } = await supabase.from("division_catalog").insert(rows).select();
    if (error) return toast.error(error.message);
    if (data) {
      await Promise.all(data.map((row: any) => syncSportLinks(row.id, bulkSportIds)));
    }
    toast.success(`Added ${labels.length} divisions`);
    setBulkText("");
    setBulkSportIds([]);
    setBulkOpen(false);
    loadDivisions();
  };

  const openEdit = (d: DivisionWithSports) => {
    setEditTarget(d);
    setEditLabel(d.label);
    setEditSportIds([...d.sport_ids]);
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    const label = editLabel.trim();
    if (!label) return toast.error("Enter a name");
    if (editSportIds.length === 0) return toast.error("Select at least one sport");
    const updates: any = { label };
    // keep legacy sport_id pointed at first selected sport
    updates.sport_id = editSportIds[0];
    const { error } = await supabase
      .from("division_catalog")
      .update(updates)
      .eq("id", editTarget.id);
    if (error) return toast.error(error.message);
    await syncSportLinks(editTarget.id, editSportIds);
    toast.success("Updated");
    setEditTarget(null);
    loadDivisions();
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const list = filteredDivisions;
    const oldIndex = list.findIndex((x) => x.id === active.id);
    const newIndex = list.findIndex((x) => x.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(list, oldIndex, newIndex);
    // Reuse the existing display_order slots of the visible rows, in ascending order
    const slots = list.map((d) => d.display_order).sort((a, b) => a - b);

    // Optimistic local update
    const orderById = new Map<string, number>();
    reordered.forEach((d, i) => orderById.set(d.id, slots[i]));
    setDivisions((prev) =>
      [...prev]
        .map((d) => (orderById.has(d.id) ? { ...d, display_order: orderById.get(d.id)! } : d))
        .sort((a, b) => a.display_order - b.display_order)
    );

    const updates = reordered
      .filter((d, i) => d.display_order !== slots[i])
      .map((d, i) => ({ id: d.id, order: orderById.get(d.id)! }));

    const results = await Promise.all(
      updates.map(({ id, order }) =>
        supabase.from("division_catalog").update({ display_order: order }).eq("id", id)
      )
    );
    if (results.some((r: any) => r.error)) {
      toast.error("Failed to save order");
      loadDivisions();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase
      .from("division_catalog")
      .update({ is_active: false })
      .eq("id", deleteTarget.id);
    if (error) return toast.error(error.message);
    toast.success(`Deleted "${deleteTarget.label}"`);
    setDeleteTarget(null);
    loadDivisions();
  };

  const toggleInArray = (arr: string[], id: string, setter: (v: string[]) => void) => {
    setter(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Divisions</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage age groups and divisions. Each division can be assigned to one or more sports.
        </p>
      </div>

      {/* Sport filter — only sports that have divisions */}
      {sportsWithDivisions.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={activeFilterSportId === "all" ? "default" : "outline"}
            onClick={() => setActiveFilterSportId("all")}
          >
            All ({divisions.length})
          </Button>
          {sportsWithDivisions.map((s) => {
            const count = divisions.filter((d) => d.sport_ids.includes(s.id)).length;
            return (
              <Button
                key={s.id}
                size="sm"
                variant={activeFilterSportId === s.id ? "default" : "outline"}
                onClick={() => setActiveFilterSportId(s.id)}
              >
                {s.name} ({count})
              </Button>
            );
          })}
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filteredDivisions.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No divisions yet. Add one below.
            </p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredDivisions.map((d) => d.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="border border-border rounded-lg divide-y divide-border">
                {filteredDivisions.map((d, idx) => (
                  <SortableDivisionRow
                    key={d.id}
                    division={d}
                    index={idx}
                    sportName={sportName}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Add single */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <div className="text-sm font-medium">Add a division</div>
          <Input
            placeholder="Name (e.g. 14U)"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-2">Assign to sports</div>
            <div className="flex flex-wrap gap-3">
              {sports.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={newSportIds.includes(s.id)}
                    onCheckedChange={() => toggleInArray(newSportIds, s.id, setNewSportIds)}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={handleAdd}>
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>

        {/* Bulk add */}
        <div className="border border-border rounded-lg">
          <button
            onClick={() => setBulkOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-secondary"
          >
            <span>Add multiple at once</span>
            {bulkOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {bulkOpen && (
            <div className="px-4 py-3 border-t border-border space-y-3">
              <Textarea
                rows={6}
                placeholder={"One label per line\n14U\n15U\n16U"}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
              />
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  Assign all to sports
                </div>
                <div className="flex flex-wrap gap-3">
                  {sports.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={bulkSportIds.includes(s.id)}
                        onCheckedChange={() => toggleInArray(bulkSportIds, s.id, setBulkSportIds)}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
              <Button onClick={handleBulkAdd} disabled={!bulkText.trim()}>
                Add all
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit division</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Assigned sports</label>
              <div className="flex flex-wrap gap-3">
                {sports.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={editSportIds.includes(s.id)}
                      onCheckedChange={() => toggleInArray(editSportIds, s.id, setEditSportIds)}
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete division?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deleteTarget?.label}"? Events that already use this division will keep
              showing it, but it won't appear in new events.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SortableDivisionRow({
  division: d,
  index,
  sportName,
  onEdit,
  onDelete,
}: {
  division: DivisionWithSports;
  index: number;
  sportName: (id: string) => string;
  onEdit: (d: DivisionWithSports) => void;
  onDelete: (d: DivisionWithSports) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: d.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-3 px-4 py-3 bg-card ${
        isDragging ? "relative z-10 shadow-lg opacity-90" : ""
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${d.label}`}
        className={`touch-none text-muted-foreground hover:text-foreground ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-xs text-muted-foreground w-8">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground">{d.label}</div>
        <div className="text-xs text-muted-foreground truncate">
          {d.sport_ids.length === 0
            ? "No sport assigned"
            : d.sport_ids.map(sportName).filter(Boolean).join(", ")}
        </div>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onEdit(d)}>
        <Pencil className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onDelete(d)}
        className="text-destructive hover:text-destructive"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}
